import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Loader2, Save, Upload, Trash2, Building2, Info } from "lucide-react";

const FIELDS = [
  { key: "companyName", label: "Firmenname *", ph: "Muster GmbH" },
  { key: "street", label: "Straße und Hausnummer", ph: "Musterstraße 1" },
  { key: "zipCity", label: "PLZ und Ort", ph: "10115 Berlin" },
  { key: "country", label: "Land", ph: "Deutschland" },
  { key: "email", label: "E-Mail", ph: "info@muster.de" },
  { key: "phone", label: "Telefon", ph: "+49 ..." },
  { key: "website", label: "Webseite", ph: "www.muster.de" },
];

export default function MyCompany() {
  const [form, setForm] = useState({});
  const [logo, setLogo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const fileRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/profile/branding");
        const { logo: lg, ...rest } = r.data;
        setForm(rest);
        setLogo(lg);
      } catch (e) {
        setError(formatApiError(e.response?.data?.detail) || e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const flash = (msg) => { setSuccess(msg); setTimeout(() => setSuccess(""), 3000); };

  const save = async () => {
    setSaving(true); setError("");
    try {
      await api.put("/profile/branding", form);
      flash("Firmendaten gespeichert.");
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    setUploading(true); setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/profile/branding/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setLogo(r.data.logo);
      flash("Logo hochgeladen.");
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setUploading(false); }
  };

  const removeLogo = async () => {
    setError("");
    try {
      await api.delete("/profile/branding/logo");
      setLogo(null);
      flash("Logo entfernt.");
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  if (loading) return <div className="p-10 text-sm text-zinc-500">Wird geladen...</div>;

  return (
    <div className="p-4 md:p-10 max-w-2xl" data-testid="my-company-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Profil</div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight text-zinc-900 flex items-center gap-2">
            <Building2 size={24}/> Meine Firma
          </h1>
        </div>
        <button onClick={save} disabled={saving} data-testid="branding-save-button"
                className="bg-zinc-900 hover:bg-zinc-800 text-white text-sm font-medium px-4 py-2.5 rounded-sm flex items-center gap-2 disabled:opacity-60">
          {saving ? <Loader2 size={14} className="animate-spin"/> : <Save size={14}/>} Speichern
        </button>
      </div>

      <div className="text-xs text-zinc-600 bg-zinc-50 border border-zinc-200 px-3 py-2.5 rounded-sm mb-6 flex items-start gap-2">
        <Info size={14} className="mt-0.5 shrink-0"/>
        <span>Ihr Logo und Ihre Firmendaten erscheinen auf allen Ihren Angebots-PDFs (Kopf- und Fußzeile).
        Solange kein Firmenname eingetragen ist, wird der Standard-Briefkopf verwendet.</span>
      </div>

      {error && <div className="text-xs font-medium text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-sm mb-4" data-testid="branding-error">{error}</div>}
      {success && <div className="text-xs font-medium text-green-700 bg-green-50 border border-green-200 px-3 py-2 rounded-sm mb-4" data-testid="branding-success">{success}</div>}

      {/* Logo */}
      <section className="mb-8">
        <h2 className="text-sm font-semibold uppercase tracking-widest text-zinc-700 mb-3 pb-2 border-b border-zinc-200">Logo</h2>
        <div className="flex items-center gap-4">
          <div className="w-40 h-24 border border-zinc-200 bg-white rounded-sm flex items-center justify-center overflow-hidden">
            {logo ? (
              <img src={logo} alt="Logo" className="max-w-full max-h-full object-contain" data-testid="branding-logo-preview"/>
            ) : (
              <span className="text-xs text-zinc-400">Kein Logo</span>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
                   data-testid="branding-logo-input"
                   onChange={(e) => uploadLogo(e.target.files?.[0])}/>
            <button onClick={() => fileRef.current?.click()} disabled={uploading}
                    data-testid="branding-logo-upload-button"
                    className="text-xs border border-zinc-300 px-3 py-2 rounded-sm hover:bg-zinc-50 flex items-center gap-1.5 disabled:opacity-50">
              {uploading ? <Loader2 size={12} className="animate-spin"/> : <Upload size={12}/>}
              Logo hochladen (PNG/JPG, max. 2 MB)
            </button>
            {logo && (
              <button onClick={removeLogo} data-testid="branding-logo-delete-button"
                      className="text-xs text-red-600 hover:text-red-700 flex items-center gap-1.5">
                <Trash2 size={12}/> Logo entfernen
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Firmendaten */}
      <section>
        <h2 className="text-sm font-semibold uppercase tracking-widest text-zinc-700 mb-3 pb-2 border-b border-zinc-200">Firmendaten (Briefkopf)</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {FIELDS.map(({ key, label, ph }) => (
            <div key={key} className={key === "companyName" ? "md:col-span-2" : ""}>
              <label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 mb-1 block">{label}</label>
              <input value={form[key] || ""} placeholder={ph}
                     onChange={(e) => setForm((p) => ({ ...p, [key]: e.target.value }))}
                     data-testid={`branding-${key}`}
                     className="w-full px-3 py-2 bg-white border border-zinc-300 rounded-sm text-sm focus:border-black focus:ring-1 focus:ring-black"/>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
